import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Modal, Button, FormRow } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { LuRefreshCw } from 'react-icons/lu';
import { FaArrowRight, FaArrowLeft, FaCheck } from 'react-icons/fa';
import {
  MdPeople, MdStickyNote2, MdForum, MdAssignment, MdEventNote, MdHandshake, MdEditNote,
} from 'react-icons/md';
import PropTypes from 'prop-types';
import { syncProjectToOplaCrm, syncFromOplaCrm, getProject } from 'redux/crm/projectSlice';
import { getActivities, getTasks, getNotes } from 'redux/crm/project/projectActivitySlice';
import { getStageDiscussions } from 'redux/crm/project/stageDiscussionSlice';
import { SELECT_STYLES } from 'helpers/formStyles';

// How long (ms) to wait before refreshing — gives the Sidekiq job time to run.
const REFRESH_DELAY_MS = 1000;

// Map sync_type → which refresh dispatches to fire
const REFRESH_MAP = {
  activities:   (projectId, dispatch) => dispatch(getActivities({ projectId, page: 1, per_page: 20 })),
  tasks:        (projectId, dispatch) => dispatch(getTasks({ projectId, page: 1, per_page: 20 })),
  notes:        (projectId, dispatch) => dispatch(getNotes({ projectId, page: 1, per_page: 15 })),
  discussions:  (projectId, dispatch) => dispatch(getStageDiscussions({ projectId, stage: 'all' })),
  team_members: (projectId, dispatch) => dispatch(getProject(projectId)),
  // Related parties write consultant/contractor/enduser onto the project itself
  related_parties: (projectId, dispatch) => dispatch(getProject(projectId)),
};

const STATUS_LABEL = { OPEN: 'Open', CLOSE_WON: 'Close Won', CLOSE_LOST: 'Close Lost' };

const SYNC_TYPES = [
  { key: 'activities',   label: 'Activities',   Icon: MdEventNote,   color: '#3b82f6' },
  { key: 'notes',        label: 'Notes',        Icon: MdStickyNote2, color: '#f59e0b' },
  { key: 'discussions',  label: 'Discussions',  Icon: MdForum,       color: '#8b5cf6' },
  { key: 'tasks',        label: 'Tasks',        Icon: MdAssignment,  color: '#ef4444' },
  { key: 'team_members', label: 'Team Members', Icon: MdPeople,      color: '#10b981' },
  { key: 'related_parties', label: 'Related Parties', Icon: MdHandshake, color: '#0ea5e9' },
  // Project name + customer + contact (Opla sends no event when they change).
  { key: 'details',      label: 'Name & Customer', Icon: MdEditNote,  color: '#64748b' },
];

// ── Tabs ──────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'push', label: 'Push stage → Opla',  icon: FaArrowRight, desc: 'Update Opla CRM with the current sales stage' },
  { key: 'pull', label: 'Pull data ← Opla',   icon: FaArrowLeft,  desc: 'Import activities, notes and more from Opla CRM' },
];

const SyncToOplaCrmForm = ({ showModal, setshowModal, project }) => {
  const { control, handleSubmit, formState: { errors }, setValue } = useForm();
  const dispatch = useDispatch();

  const [tab,           setTab]           = useState('push');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [submitting,    setSubmitting]    = useState(false);
  const [,              setCountdown]     = useState(null);  // null | number
  const countdownRef = useRef(null);

  const appSettings  = useSelector(s => s.app_settings?.data || []);
  const salesProcess = useMemo(() => {
    const setting = appSettings.find(s => s.key === 'OPLA_SALES_PROCESS');
    if (!setting?.value) return null;
    try { return JSON.parse(setting.value); } catch { return null; }
  }, [appSettings]);

  const stageOptions = useMemo(() => (salesProcess?.stages || [])
    .slice()
    .sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0))
    .map(s => ({ value: { id: s.id, name: s.name }, label: s.name, status: s.status }))
  , [salesProcess]);

  const closeModal = () => {
    clearInterval(countdownRef.current);
    setshowModal(false);
    setSelectedTypes([]);
    setTab('push');
    setCountdown(null);
  };

  // Schedule refreshes after REFRESH_DELAY_MS with a visible toast countdown.
  // The job runs async in Sidekiq — wait ~3 s before re-fetching.
  const scheduleRefresh = (types) => {
    const secs = Math.ceil(REFRESH_DELAY_MS / 1000);
    setCountdown(secs);
    let remaining = secs;
    const toastId = toast.info(`Refreshing data in ${remaining}s…`, {
      autoClose:    false,
      closeOnClick: false,
    });
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(countdownRef.current);
        setCountdown(null);
        toast.dismiss(toastId);
        const refreshSet = new Set([...types]);
        refreshSet.forEach(t => REFRESH_MAP[t]?.(project.id, dispatch));
        dispatch(getProject(project.id));
        toast.success('Data refreshed.');
      } else {
        setCountdown(remaining);
        toast.update(toastId, { render: `Refreshing data in ${remaining}s…` });
      }
    }, 1000);
  };

  // Clean up interval on unmount
  useEffect(() => () => clearInterval(countdownRef.current), []);
  const toggleType  = (key) => setSelectedTypes(p => p.includes(key) ? p.filter(k => k !== key) : [...p, key]);
  const allSelected = selectedTypes.length === SYNC_TYPES.length;

  const onSubmitStage = async (data) => {
    try {
      setSubmitting(true);
      const r = await dispatch(syncProjectToOplaCrm({
        projectId: project.id,
        data: { stage_id: data.stage?.id, stage_name: data.stage?.name },
      }));
      toast.success(r.payload?.message);
      closeModal();
      scheduleRefresh([]);  // just refresh project header
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const onPullFromOpla = async () => {
    if (!selectedTypes.length) { toast.warning('Select at least one data type.'); return; }
    try {
      setSubmitting(true);
      const r = await dispatch(syncFromOplaCrm({ projectId: project.id, syncTypes: selectedTypes }));
      toast.success(r.payload?.message || 'Sync queued — refreshing in 3s…');
      closeModal();
      scheduleRefresh(selectedTypes);  // refresh each selected data slice
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const formatOptionLabel = ({ label, status }) => (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
      <span className="tw-text-sm">{label}</span>
      {status && (
        <span className={`tw-text-[10px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-border ${
          status === 'CLOSE_WON'  ? 'tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-200' :
          status === 'CLOSE_LOST' ? 'tw-bg-red-50 tw-text-red-700 tw-border-red-200' :
                                    'tw-bg-blue-50 tw-text-blue-700 tw-border-blue-200'
        }`}>{STATUS_LABEL[status] || status}</span>
      )}
    </div>
  );

  const isPush = tab === 'push';

  const footer = (
    <div className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-justify-end">
      <Button variant="ghost" size="sm" onClick={closeModal} disabled={submitting}>Cancel</Button>
      {isPush ? (
        <Button size="sm" onClick={handleSubmit(onSubmitStage)} disabled={submitting}>
          <LuRefreshCw size={14} /> Sync stage to Opla
        </Button>
      ) : (
        <Button size="sm" onClick={onPullFromOpla} disabled={submitting || !selectedTypes.length}>
          <FaArrowLeft size={12} />
          Import{selectedTypes.length > 0 ? ` (${selectedTypes.length})` : ''}
        </Button>
      )}
    </div>
  );

  return (
    <Modal show={showModal} onHide={closeModal} title="Sync to Opla CRM" backdropClick={false} footer={footer}>
      <div className="tw-flex tw-flex-col tw-gap-4">

        {/* Opla ID */}
        <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded tw-bg-slate-50 tw-border tw-border-slate-200">
          <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400 tw-shrink-0">Opla ID</span>
          <span className="tw-text-[12px] tw-font-mono tw-font-semibold tw-text-slate-700 tw-truncate">{project?.opla_id || '—'}</span>
        </div>

        {/* ── Tab switcher ── */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-2">
          {TABS.map(({ key, label, icon: Icon, desc }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`tw-flex tw-flex-col tw-items-start tw-gap-1 tw-px-4 tw-py-3 tw-rounded-lg tw-border-2 tw-cursor-pointer tw-transition-all tw-text-left ${
                  active
                    ? 'tw-border-blue-500 tw-bg-blue-50'
                    : 'tw-border-slate-200 tw-bg-white hover:tw-border-slate-300 hover:tw-bg-slate-50'
                }`}
              >
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Icon size={12} className={active ? 'tw-text-blue-600' : 'tw-text-slate-400'} />
                  <span className={`tw-text-[12px] tw-font-bold ${active ? 'tw-text-blue-700' : 'tw-text-slate-600'}`}>
                    {label}
                  </span>
                </div>
                <span className={`tw-text-[11px] tw-leading-snug ${active ? 'tw-text-blue-500' : 'tw-text-slate-400'}`}>
                  {desc}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Tab content ── */}
        <div className="tw-bg-slate-50 tw-rounded-lg tw-border tw-border-slate-200 tw-p-4">

          {/* PUSH tab */}
          {isPush && (
            <div className="tw-flex tw-flex-col tw-gap-3">
              <div className="tw-flex tw-items-center tw-gap-2 tw-text-[11px] tw-text-amber-700 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded tw-px-3 tw-py-2">
                <span>⚠</span>
                <span>This will update the sales pipeline stage on Opla CRM.</span>
              </div>

              {!salesProcess ? (
                <p className="tw-text-xs tw-text-slate-500 tw-italic tw-mb-0">
                  No sales process configured. Please sync <strong>OPLA_SALES_PROCESS</strong> from App Settings first.
                </p>
              ) : (
                <FormRow label="Stage" required error={errors.stage?.message}>
                  <Controller
                    control={control}
                    name="stage"
                    rules={{ required: 'Stage is required' }}
                    render={() => (
                      <Select
                        closeMenuOnSelect
                        options={stageOptions}
                        placeholder="Select stage..."
                        menuPosition="fixed"
                        styles={SELECT_STYLES}
                        formatOptionLabel={formatOptionLabel}
                        onChange={opt => setValue('stage', opt.value, { shouldValidate: true })}
                      />
                    )}
                  />
                </FormRow>
              )}
            </div>
          )}

          {/* PULL tab */}
          {!isPush && (
            <div className="tw-flex tw-flex-col tw-gap-3">
              <div className="tw-flex tw-items-center tw-gap-2 tw-text-[11px] tw-text-blue-700 tw-bg-blue-50 tw-border tw-border-blue-100 tw-rounded tw-px-3 tw-py-2">
                <span>ℹ</span>
                <span>Select data types to import from Opla CRM into the Portal.</span>
              </div>

              {/* Select all */}
              <div className="tw-flex tw-items-center tw-justify-between">
                <span className="tw-text-[11px] tw-text-slate-500 tw-font-medium">
                  {selectedTypes.length} of {SYNC_TYPES.length} selected
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedTypes(allSelected ? [] : SYNC_TYPES.map(t => t.key))}
                  className="tw-text-[11px] tw-font-semibold tw-text-blue-500 hover:tw-text-blue-700 tw-border-0 tw-bg-transparent tw-cursor-pointer"
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </button>
              </div>

              {/* Type list */}
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                {SYNC_TYPES.map(({ key, label, Icon, color }) => {
                  const checked = selectedTypes.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleType(key)}
                      className={`tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-rounded tw-border tw-cursor-pointer tw-transition-all tw-text-left ${
                        checked
                          ? 'tw-bg-white tw-border-current tw-shadow-sm'
                          : 'tw-bg-white tw-border-slate-200 hover:tw-border-slate-300'
                      }`}
                      style={checked ? { borderColor: color } : {}}
                    >
                      {/* Check */}
                      <span className={`tw-flex tw-items-center tw-justify-center tw-w-4.5 tw-h-4.5 tw-shrink-0 tw-rounded-sm tw-border ${
                        checked ? 'tw-border-current' : 'tw-border-slate-300 tw-bg-white'
                      }`} style={checked ? { backgroundColor: color, borderColor: color } : {}}>
                        {checked && <FaCheck size={8} className="tw-text-white" />}
                      </span>

                      {/* Icon */}
                      <span className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded tw-shrink-0"
                        style={{ backgroundColor: `${color}15` }}>
                        <Icon size={14} style={{ color }} />
                      </span>

                      {/* Label */}
                      <span className={`tw-text-[13px] tw-font-medium tw-flex-1 ${checked ? 'tw-text-slate-800' : 'tw-text-slate-500'}`}>
                        {label}
                      </span>

                      {checked && (
                        <span className="tw-text-[10px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded-xl"
                          style={{ backgroundColor: `${color}15`, color }}>
                          Selected
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
};

SyncToOplaCrmForm.propTypes = {
  showModal:    PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  project:      PropTypes.object.isRequired,
};

export default SyncToOplaCrmForm;
